import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listUsers, updateUserRoles } from '../api';
import type { UserListItem } from '../api';
import { networkManager } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Loader2, Search, Check, Download, Users } from 'lucide-react';

const ALL_ROLES = ['super_admin', 'admin', 'organizer', 'participant', 'judge', 'mentor'];
const BULK_ASSIGNABLE_ROLES = ['admin', 'organizer', 'participant', 'judge', 'mentor']; // super_admin excluded from bulk ops

/** Matches backend POST /api/bulk/roles response */
interface BulkRolesResult {
  updated: number;
  failed: number;
  errors: Array<{ userId: string; error: string }>;
}

export function AdminUsersPage() {
  const { can } = useAuth();
  const canBulkAssign = can('bulk:assign');
  const canBulkExport = can('bulk:export');

  // Search query is kept in the URL (?q=...) so bookmarks, refresh,
  // and browser back/forward preserve it.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get('q') ?? '';

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(urlSearch);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const commitSearchToUrl = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const trimmed = value.trim();
          if (trimmed.length >= 2) {
            params.set('q', trimmed);
          } else {
            params.delete('q');
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRole, setBulkRole] = useState('');
  const [bulkAction, setBulkAction] = useState<'add' | 'remove'>('add');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkRolesResult | null>(null);
  const [exportingUsers, setExportingUsers] = useState(false);

  function loadUsers(query?: string) {
    setLoading(true);
    listUsers({ search: query && query.length >= 2 ? query : undefined })
      .then((res) => setUsers(res.users))
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  // Initial load + reload when the URL search param changes
  // (e.g. via browser back/forward)
  useEffect(() => {
    loadUsers(urlSearch);
  }, [urlSearch]);

  function handleSearch() {
    commitSearchToUrl(search);
  }

  function startEdit(user: UserListItem) {
    setEditingUser(user);
    setEditRoles([...user.roles]);
  }

  function toggleRole(role: string) {
    setEditRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  async function saveRoles() {
    if (!editingUser) return;
    setSaving(true);
    try {
      await updateUserRoles(editingUser.sub, editRoles);
      setUsers((prev) =>
        prev.map((u) => (u.sub === editingUser.sub ? { ...u, roles: editRoles } : u)),
      );
      setEditingUser(null);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  function toggleSelection(userId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((u) => u.sub)));
    }
  }

  async function handleBulkRoles() {
    if (selectedIds.size === 0 || !bulkRole) return;
    setBulkSaving(true);
    setBulkResult(null);
    try {
      const res = await networkManager.post<{ status: boolean; data: BulkRolesResult }>(
        '/api/bulk/roles',
        {
          userIds: Array.from(selectedIds),
          role: bulkRole,
          action: bulkAction,
        },
      );
      setBulkResult(res.data);
      // Refresh user list
      loadUsers(search);
      setSelectedIds(new Set());
    } catch {
      setBulkResult({ updated: 0, failed: selectedIds.size, errors: [] });
    } finally {
      setBulkSaving(false);
    }
  }

  function handleExportUsers() {
    setExportingUsers(true);
    try {
      const blob = new Blob([JSON.stringify(users, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingUsers(false);
    }
  }

  return (
    <div className="animate-fade-in">
      <Link to="/" className="btn-ghost mb-4 inline-flex">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </Link>

      <h1 className="font-display text-3xl font-bold text-ink">Users</h1>

      <div className="mt-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-tertiary" />
          <input
            type="text"
            className="input-base pl-9"
            placeholder="Search by name or email (min 2 chars)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <button className="btn-secondary" onClick={handleSearch}>
          Search
        </button>
        {canBulkExport && (
          <button
            className="btn-secondary inline-flex items-center gap-1.5"
            onClick={handleExportUsers}
            disabled={exportingUsers || users.length === 0}
          >
            {exportingUsers ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {canBulkAssign && selectedIds.size > 0 && (
        <div className="mt-3 card flex flex-wrap items-center gap-3 p-3 border-blue-200 bg-blue-50">
          <span className="text-sm font-medium text-blue-800">
            <Users className="inline h-4 w-4 mr-1" />
            {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <select
            className="input-base w-auto text-sm"
            value={bulkRole}
            onChange={(e) => setBulkRole(e.target.value)}
          >
            <option value="">Select role...</option>
            {BULK_ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select
            className="input-base w-auto text-sm"
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value as 'add' | 'remove')}
          >
            <option value="add">Add role</option>
            <option value="remove">Remove role</option>
          </select>
          <button
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            onClick={handleBulkRoles}
            disabled={bulkSaving || !bulkRole}
          >
            {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Apply
          </button>
          <button
            className="btn-ghost text-sm"
            onClick={() => { setSelectedIds(new Set()); setBulkResult(null); }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Bulk result */}
      {bulkResult && (
        <div className={`mt-2 rounded-lg border p-2 text-sm ${bulkResult.failed > 0 ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-green-200 bg-green-50 text-green-800'}`}>
          Updated {bulkResult.updated} user{bulkResult.updated !== 1 ? 's' : ''}{bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ''}
        </div>
      )}

      {loading ? (
        <div className="mt-8 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
        </div>
      ) : (
        <div className="mt-4 card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ring bg-surface-sunken">
                {canBulkAssign && (
                  <th className="w-10 px-3 py-2.5">
                    <div
                      className={`flex h-5 w-5 cursor-pointer items-center justify-center rounded border ${
                        selectedIds.size === users.length && users.length > 0
                          ? 'border-accent bg-accent text-ink-inverse'
                          : 'border-ring'
                      }`}
                      onClick={toggleSelectAll}
                    >
                      {selectedIds.size === users.length && users.length > 0 && <Check className="h-3 w-3" />}
                    </div>
                  </th>
                )}
                <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Name</th>
                <th className="hidden px-4 py-2.5 text-left font-medium text-ink-secondary sm:table-cell">
                  Email
                </th>
                <th className="px-4 py-2.5 text-left font-medium text-ink-secondary">Roles</th>
                <th className="px-4 py-2.5 text-right font-medium text-ink-secondary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.sub} className={`border-b border-ring last:border-0 ${selectedIds.has(user.sub) ? 'bg-blue-50/50' : ''}`}>
                  {canBulkAssign && (
                    <td className="w-10 px-3 py-2.5">
                      <div
                        className={`flex h-5 w-5 cursor-pointer items-center justify-center rounded border ${
                          selectedIds.has(user.sub)
                            ? 'border-accent bg-accent text-ink-inverse'
                            : 'border-ring'
                        }`}
                        onClick={() => toggleSelection(user.sub)}
                      >
                        {selectedIds.has(user.sub) && <Check className="h-3 w-3" />}
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-ink">{user.name || '—'}</td>
                  <td className="hidden px-4 py-2.5 text-ink-secondary sm:table-cell">
                    {user.email || '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <span
                          key={role}
                          className="rounded bg-accent-subtle px-1.5 py-0.5 text-2xs font-medium text-ink"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button className="btn-ghost text-sm" onClick={() => startEdit(user)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={canBulkAssign ? 5 : 4} className="px-4 py-8 text-center text-ink-secondary">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card mx-4 w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-ink">
              Edit Roles: {editingUser.name || editingUser.email}
            </h2>
            <div className="mt-4 space-y-2">
              {ALL_ROLES.map((role) => (
                <label
                  key={role}
                  className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-surface-sunken"
                >
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded border ${
                      editRoles.includes(role)
                        ? 'border-accent bg-accent text-ink-inverse'
                        : 'border-ring'
                    }`}
                    onClick={() => toggleRole(role)}
                  >
                    {editRoles.includes(role) && <Check className="h-3 w-3" />}
                  </div>
                  <span className="text-sm text-ink">{role}</span>
                </label>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => setEditingUser(null)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={saveRoles} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save Roles
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
